import React from 'react';
import createPalette from './generators/palette.js';
import createTypography from './generators/typography.js';
import createCustomStyles from './generators/customStyles.js';
import { defaultTheme } from './defaults.js';
import { u as utils } from '../themeUtils-70529429.js';
import './generators/identifiers.js';
import './palettes/presets.js';
import './palettes/wixPaletteUtils.js';
import 'lodash';

var lib = {};

Object.defineProperty(lib, "__esModule", { value: true });
lib.hasValueAtKey = lib.hasPresentKey = lib.isFilled = isDefined_1 = lib.isDefined = lib.isPresent = void 0;
function isPresent(t) {
    return t !== undefined && t !== null;
}
lib.isPresent = isPresent;
function isDefined(t) {
    return t !== undefined;
}
var isDefined_1 = lib.isDefined = isDefined;
function isFilled(t) {
    return t !== null;
}
lib.isFilled = isFilled;
/**
 * Returns a function that can be used to filter down objects
 * to the ones that have a defined non-null value under the key `k`.
 *
 * @example
 * ```ts
 * const filesWithUrl = files.filter(file => file.url);
 * files[0].url // In this case, TS might still treat this as undefined/null
 *
 * const filesWithUrl = files.filter(hasPresentKey("url"));
 * files[0].url // TS will know that this is present
 * ```
 *
 * See https://github.com/microsoft/TypeScript/issues/16069
 * why is that useful.
 */
function hasPresentKey(k) {
    return function (a) {
        return a[k] !== undefined && a[k] !== null;
    };
}
lib.hasPresentKey = hasPresentKey;
/**
 * Returns a function that can be used to filter down objects
 * to the ones that have a specific value V under a key `k`.
 *
 * @example
 * ```ts
 * type File = { type: "image", imageUrl: string } | { type: "pdf", pdfUrl: string };
 * const files: File[] = [];
 *
 * const imageFiles = files.filter(file => file.type === "image");
 * files[0].type // In this case, TS will still treat it  as `"image" | "pdf"`
 *
 * const filesWithUrl = files.filter(hasValueKey("type", "image" as const));
 * files[0].type // TS will now know that this is "image"
 * files[0].imageUrl // TS will know this is present, because already it excluded the other union members.
 *
 * Note: the cast `as const` is necessary, otherwise TS will only know that type is a string.
 * ```
 *
 * See https://github.com/microsoft/TypeScript/issues/16069
 * why is that useful.
 */
function hasValueAtKey(k, v) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return function (a) {
        return a[k] === v;
    };
}
lib.hasValueAtKey = hasValueAtKey;

var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
function themeStrategy(args) {
  var _a, _b, _c, _d, _e, _f;
  const { buildCssVars } = utils;
  const { ricosTheme = {}, plugins = [], cssOverride = {} } = args;
  const {
    parentClass = "",
    palette,
    paletteConfig,
    typography,
    customStyles,
    settingsStyles,
    nativeStyle,
    breakout
  } = ricosTheme;
  const themeGeneratorFunctions = plugins.map((plugin) => plugin.theme).filter(isDefined_1);
  const { paletteVarsObject, colors } = createPalette(palette, paletteConfig);
  const typographyVarsObject = createTypography(typography);
  const customsVarsObject = createCustomStyles({
    customStyles,
    settingsStyles,
    nativeStyle
  });
  const breakoutVars = breakout ? {
    "breakout-normal-padding-start": (_a = breakout == null ? void 0 : breakout.normalPadding.start) != null ? _a : "0",
    "breakout-normal-padding-end": (_b = breakout == null ? void 0 : breakout.normalPadding.end) != null ? _b : "0",
    "breakout-full-width-padding-start": (_d = (_c = breakout == null ? void 0 : breakout.fullWidthPadding) == null ? void 0 : _c.start) != null ? _d : "0",
    "breakout-full-width-padding-end": (_f = (_e = breakout == null ? void 0 : breakout.fullWidthPadding) == null ? void 0 : _e.end) != null ? _f : "0"
  } : {};
  const themeData = {
    colors,
    utils,
    customStyles
  };
  if (colors) {
    themeGeneratorFunctions.forEach((themeGen) => themeGen({ colors, utils, customStyles }));
  }
  const html = /* @__PURE__ */ React.createElement("style", { type: "text/css", key: "styleElement" }, buildCssVars(
    parentClass,
    paletteVarsObject,
    typographyVarsObject,
    customsVarsObject,
    breakoutVars
  ));
  return {
    theme: __spreadValues(__spreadValues({}, defaultTheme), cssOverride),
    html,
    themeData
  };
}

export { themeStrategy as default };
//# sourceMappingURL=themeStrategy.js.map
